import * as React from 'react';
import HistoryTabs from 'Components/HistoryTabs';
import TaskListHistory from './TaskListHistory';
import { useAppSelector as useSelector } from 'Store/index';
import SurveysHistory from './SurveysHistory';
import InvitesHistory from './InvitesHistory';
import { equals } from 'ramda';
import { informantSurveysByPlatform, surveysByPlatform } from '../config';
import SuppliesHistory from './SuppliesHistory';
import { PatientMyServiceStatus } from './components/PatientMyServiceStatus';
import { filterDocumentsByType } from 'Utility/documentHandling';
import { DateTime } from 'luxon';
import { TASK_DATE_FORMAT } from 'Utility/tasklist';

const MyServiceHistory = ({ selectedTab, setSelectedTab, inviteErrors }: IOwnProps): React.JSX.Element => {
  const [onGoingTasks, setOnGoingTasks] = React.useState<Array<ITaskList>>([]);

  const platform = useSelector(s => s.session.platforms?.selected);
  const { taskLists, sentInvites: invites } = useSelector(s => s.myapp);
  const sortedAndMergedDocs = useSelector(s => s.documents.sortedAndMergedDocuments) ?? [];
  const sortedAndMergedMyDocs = useSelector(s => s.myapp.sortedAndMergedDocuments);

  React.useEffect(() => {
    const ongTasks = taskLists.filter(
      tl => tl.end_exclusive && DateTime.fromFormat(tl.end_exclusive, TASK_DATE_FORMAT) > DateTime.now(),
    );
    if (!equals(onGoingTasks, ongTasks)) setOnGoingTasks(ongTasks);
  }, [taskLists]);

  React.useEffect(() => {
    if (selectedTab >= tabs.length) {
      setSelectedTab(0);
    }
  }, [platform]);

  const surveyDocs: Array<IDocumentWithTypeV2<TMyDocumentType>> = sortedAndMergedMyDocs.filter(
    (d): d is IDocumentWithTypeV2<TMyDocumentType> => {
      if (!platform) return false;
      const surveys: TDocumentType[] | undefined = surveysByPlatform[platform];
      const informantSurveys: TDocumentType[] | undefined = informantSurveysByPlatform[platform];
      // ehh
      return Boolean(
        (surveys?.filter(t => (platform === 'ninmt' ? t !== 'supplyShortagesInquiry' : t)).includes(d._type) ||
          informantSurveys?.includes(d._type)) &&
        (d._type.toLowerCase().includes('background') || d._type === 'parkinsonLifestyle' ? false : true),
      );
    },
  );

  const treatmentDecisionDocs =
    platform === 'ninmt' ? filterDocumentsByType(sortedAndMergedDocs, ['ninmtTreatmentPeriod']) : [];

  const supplyShortagesInquiryDocs =
    platform === 'ninmt' ? filterDocumentsByType(sortedAndMergedMyDocs, ['supplyShortagesInquiry']) : [];

  const tabs = [
    {
      title: 'myService.surveys',
      content: (
        <SurveysHistory
          treatmentDecisionDocs={treatmentDecisionDocs}
          surveyDocs={surveyDocs}
          onGoingTasks={onGoingTasks}
          taskLists={taskLists}
        />
      ),
    },
    {
      title: 'myService.taskLists',
      content: <TaskListHistory treatmentDecisionDocs={treatmentDecisionDocs} taskLists={taskLists} />,
    },
    {
      title: 'myService.invites',
      content: (
        <InvitesHistory
          invites={invites}
          inviteErrors={inviteErrors}
          promTasks={taskLists.filter(tasklist => {
            if (tasklist.task_kind === 'PROM') return true;
            if (!tasklist.task_kind && !tasklist.informant_id) return true;
            return false;
          })}
        />
      ),
    },
    {
      title: 'myService.supplies',
      content: <SuppliesHistory supplyShortagesInquiryDocs={supplyShortagesInquiryDocs} />,
    },
  ].slice(
    ['ninmt', 'sleepApnea', 'sma', 'dmd', 'parkinson', 'mgravis'].includes(platform ?? '')
      ? 0
      : platform === 'ms'
        ? 1
        : 2,
    platform === 'ninmt' ? undefined : 3,
  );

  return (
    <React.Fragment>
      <PatientMyServiceStatus />
      <HistoryTabs indexSelectionTools={{ index: selectedTab, changeFunction: i => setSelectedTab(i) }}>
        {tabs}
      </HistoryTabs>
    </React.Fragment>
  );
};

interface IOwnProps {
  selectedTab: number;
  setSelectedTab: (tab: number) => void;
  inviteErrors: Array<{ method: ContactInfoType; statusCode: number }>;
}

export default MyServiceHistory;
